import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, EVENT_ID, capturePreviewEventFromUrl } from "@/lib/api";
import { useAuthCtx } from "@/context/AuthContext";

// Capture a ?previewEvent= hint before anything else (no-op in Production).
capturePreviewEventFromUrl();

const AppContext = createContext(null);
export { AppContext };
export const useApp = () => useContext(AppContext);

const STORAGE_KEY = "collabera_persona_id";
const ADMIN_EVENT_KEY = "collabera_admin_event";

export function AppProvider({ children }) {
  const auth = useAuthCtx();
  const demo = !!auth?.demoMode;

  const [event, setEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [demoAttendeeId, setDemoAttendeeId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "att-maya"
  );
  const [adminEventId, setAdminEventIdState] = useState(
    () => localStorage.getItem(ADMIN_EVENT_KEY) || EVENT_ID
  );
  const [loading, setLoading] = useState(true);

  const refreshEvents = useCallback(() => api.listEvents().then(setEvents), []);

  // Effective attendee event: the backend-confirmed event_id from the auth
  // profile (which is the gated preview test event for authorised test identities,
  // else PC2026). Falls back to the Production default before the profile loads.
  const resolvedEventId = (!demo && auth?.profile?.event_id) || EVENT_ID;
  const previewTestEvent = !!(auth?.profile?.preview_test_event);

  useEffect(() => {
    let mounted = true;
    const tasks = [api.getEvent(resolvedEventId), api.listEvents()];
    if (demo) tasks.push(api.personas(resolvedEventId)); // persona switcher is dev-only
    Promise.all(tasks)
      .then((res) => {
        if (!mounted) return;
        setEvent(res[0]);
        setEvents(res[1]);
        if (demo && res[2]) {
          setPersonas(res[2]);
          const ids = res[2].map((p) => p.id);
          if (!ids.includes(demoAttendeeId) && res[2].length) setDemoAttendeeId(res[2][0].id);
        }
        if (!res[1].find((e) => e.id === adminEventId) && res[1].length) {
          setAdminEventIdState(res[1][0].id);
        }
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, resolvedEventId]);

  // Self-editable display profile (display_name + photo) shared across surfaces
  // so the profile chip, Home greeting and avatars reflect what the attendee
  // saved in My Profile — without touching official attendee records.
  const [myProfile, setMyProfile] = useState(null);
  const authedAttendeeId = demo ? null : (auth?.attendee?.id || null);
  const refreshMyProfile = useCallback(() => {
    if (demo || !authedAttendeeId) { setMyProfile(null); return Promise.resolve(null); }
    return api.getProfile(resolvedEventId, authedAttendeeId)
      .then((d) => { setMyProfile(d); return d; })
      .catch(() => null);
  }, [demo, authedAttendeeId, resolvedEventId]);
  useEffect(() => { refreshMyProfile(); }, [refreshMyProfile]);

  const switchPersona = useCallback((id) => {
    setDemoAttendeeId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const setAdminEvent = useCallback((id) => {
    setAdminEventIdState(id);
    localStorage.setItem(ADMIN_EVENT_KEY, id);
  }, []);

  // Identity resolution: production uses the authenticated attendee; dev uses
  // the persona switcher. Attendee pages consume attendeeId / currentPersona.
  const authedAttendee = auth?.attendee || null;
  const attendeeId = demo ? demoAttendeeId : authedAttendee?.id || null;
  const currentPersona = demo
    ? personas.find((p) => p.id === demoAttendeeId) || null
    : authedAttendee;
  const effectivePersonas = demo ? personas : authedAttendee ? [authedAttendee] : [];

  const adminEvent = events.find((e) => e.id === adminEventId) || null;

  return (
    <AppContext.Provider
      value={{
        eventId: resolvedEventId,
        previewTestEvent,
        event,
        personas: effectivePersonas,
        attendeeId,
        currentPersona,
        myProfile,
        refreshMyProfile,
        switchPersona,
        loading,
        events,
        refreshEvents,
        adminEventId,
        adminEvent,
        setAdminEvent,
        demoMode: demo,
        isManager: !!auth?.isManager,
        isAttendee: !!auth?.isAttendee,
        dualAccess: !!auth?.dualAccess,
        signOut: auth?.signOut,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// Admin pages use this so every module inherits the selected event.
export const useAdminEvent = () => {
  const ctx = useApp();
  return {
    eventId: ctx.adminEventId,
    event: ctx.adminEvent,
    events: ctx.events,
    setAdminEvent: ctx.setAdminEvent,
    refreshEvents: ctx.refreshEvents,
  };
};
